import React, { useState, useRef, useEffect, useCallback } from 'react';

import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { closeCreateKRPanelAction, openCreateKRPanelAction } from 'actions/createKRPanelActions';
import { openObjectiveDetailsPanelActionCreator } from 'actions/objectiveDetailsPanelActions';
import NamedAvatar from 'components/NamedAvatar/NamedAvatar';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import TagIcon from 'components/ObjectiveTagList/TagIcon';
import { PlayIcon } from 'components/PlayIcon';
import { openRealignModalAction } from 'components/RealignModal/redux/actions';
import StackedProgressBar from 'components/StackedProgressBar/StackedProgressBar';
import MeshDonutChart from 'components/graph/MeshDonutChart';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import {
  FlexContainer,
  StyledBox,
  StyledGrid,
  StyledIconButton,
  StyledTooltip,
  StyledTypography,
  VerticallyAlignedFlexContainer,
} from 'styles/common';
import { checkRabbitGoalsTasksFeatureFlagEnabled } from 'utils/helpers/featureFlags';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import { GOAL_PROGRESS_OPTIONS_MAP, OBJECTIVE_LEVEL_TAGS } from 'constants/ProjectConstants';
import { getColorFromKeyForAnalytics } from 'constants/helper';
import { DetailsSvgIcon, ObjectiveTwoToneSvgIcon, PlusIcon3, RealignSvgIcon, StarIcon } from 'constants/icons';
import {
  sendClickedOnCreateKRevent,
  sendClickedOnCreateObjectiveEvent,
  sendOpenedObjectivePanelEvent,
} from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { showSuccessSnackbar } from 'services/snackbar';

import { ButtonOutlined } from 'uiToolkit/Button';
import { Typography12, Typography13 } from 'uiToolkit/Typography';

import {
  DonutChartInfoView,
  HoverAbleContainerComp,
  MetricInfoView,
  MetricInfoViewForGoalsRevamp,
} from '../../components/HoverableComponents';
import {
  RabbitObjectiveDisplayComponent,
  RabbitObjectiveTableAggregatedMetricsComponent,
} from '../Team/Rabbit/Goals/Audit/rabbitTableCellComponents';
import { getSmileyComponent, RatingHover, SmileyHover } from './1on1/HoverComponents';
import {
  OKRMenuOptions,
  ColoredBgText,
  ColoredDate,
  EmptyBar,
  InfoText,
  ProgressBarWithPercentage,
  DownLine,
  Tline,
  AbsoluteWidthTextContainer,
  EngagementDonutChartHover,
} from './commonComponents';
import EmployeeHover, { EmployeeHoverContainer } from './commonComponents/EmployeeHover';
import { onObjectiveModificationSuccess } from './goals/GoalObjective/components/ObjectiveAndGoalsList';

const ColoredIconButton = styled(StyledIconButton)`
  position: relative;
  background: ${theme.palette.icons.white};
  border: 1px solid ${theme.palette.icons.blue};
  width: 24px;
  height: 24px;
`;

const StyledSpan = styled.span`
  &:hover {
    text-decoration: underline;
    color: var(--primary);
  }
`;

const RealignIconComp = ({ onClick, className }) => (
  <div className={className} style={{}}>
    <StyledTooltip title="Realign">
      <ColoredIconButton onClick={onClick}>
        <RealignSvgIcon size="fixed12px" color="primary" />
      </ColoredIconButton>
    </StyledTooltip>
  </div>
);

export const StyledRealignIconComp = styled(RealignIconComp)`
  display: inline;
  position: absolute;
  left: -0.75rem;
  visibility: hidden;
`;

const ObjectiveDisplayComponent = (props) => {
  /* component is still partially controlled as on page change the states don't reset
   and the icons on next page rows are according to the states of previous page's rows
   */

  const {
    uuid,
    value,
    level = 0,
    children,
    goals,
    expandRow = () => {},
    collapseRow = () => {},
    viewGoals = () => {},
    isLastElement,
    levelsForWhichLinesToBeDrawn,
    isExpanded = false,
    setIsExpanded = () => {},
    refetchData = () => {},
    currentPageNumber,
    expandedRowIds,
    fetchObjectiveData,
    fetchGoalsData,
    rows,
    updateRowDataModifiedForArray,
    addNewObjective,
    selectedTimeBoundPeriod,
    isTeamHubView,
    setIsOpenCreateGoalPanelObjectiveTable,
    setTimeboundDetailsNestedObjectiveTable,
    setAfterGoalCreationNestedObjectiveTable,
    timeboundDetails,
    udpateObjectiveName,
  } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const isExpandedDerived = isExpanded; // partially controlled component, will have to do complete re-write for it to be a complete controlled component

  // menu options state
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const anchorRef = useRef();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  // goal panel state
  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);

  const employee = useSelector(selectLoggedInEmployeeData);

  // objective panel state
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);

  // get time bound details for objective id

  // const [timeboundDetails, setTimeboundDetails] = useState(null);

  // timebound details of newly created objective

  const [timeboundDetailsOfCreatedObjective, setTimeboundDetailsOfCreatedObjective] = useState(null);

  const [isGoalPanelOfCreatedObjective, setIsGoalPanelOfCreatedObjective] = useState(false);

  // show realign objective or not

  const showRealignObjective = checkUserIsGoalAdmin() || employee?.isHRISManager;

  const SubTypeIcon = () => (
    <VerticallyAlignedFlexContainer style={{ marginTop: '0.5rem' }}>
      <TagIcon tagId={timeboundDetails?.objectiveLevel ? timeboundDetails?.objectiveLevel : undefined} />
      <Typography13 customColor={theme.palette.text.dark1} leftGutter={1}>
        {OBJECTIVE_LEVEL_TAGS.find(({ value }) => value === timeboundDetails?.objectiveLevel)?.label}
      </Typography13>
    </VerticallyAlignedFlexContainer>
  );

  const realignHandler = () => {
    dispatch(
      openRealignModalAction({
        objectiveId: timeboundDetails.objectiveId,
        realignId: uuid,
        objectiveName: value,
        selectedTimeBoundPeriod,
        selectedObjective: {
          timeBoundDetails: timeboundDetails.timeBoundDetails,
          objectiveId: timeboundDetails.objectiveId,
        },
        isRealignObjective: true,
        showAlignAsParent: Boolean(level),
        source: isTeamHubView ? 'Team Page' : 'Insights Page',
        onSuccess: () =>
          refetchData({
            pageNumber: currentPageNumber,
            onSuccess: () => {
              onObjectiveModificationSuccess({
                expandedRowIds,
                fetchObjectiveData,
                fetchGoalsData,
                rows,
                updateRowDataModifiedForArray,
              });
            },
          }),
      })
    );
  };

  // realign hover state
  // keeping them here cause if hovered on the hover button, the parent's hover state turns false

  const [pointerLocation, setPointerLocation] = useState({
    elem: false,
    hover: false,
  });

  const handleMouseEnter = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, elem: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, elem: false }));
  }, []);

  const handleHoverMouseEnter = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, hover: true }));
  }, []);

  const handleHoverMouseLeave = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, hover: false }));
  }, []);

  const handleLevelChange = useCallback(() => {
    udpateObjectiveName(value);
  }, [value]);

  const functionRef = useRef();
  functionRef.current = handleLevelChange;

  const handleObjectiveNameClick = (objectiveTimeBoundId) => {
    dispatch(
      openObjectiveDetailsPanelActionCreator({
        objectiveTimeBoundId,
        onNameChange: (objectiveName) => {
          udpateObjectiveName(objectiveName);
        },
        onDelete: () => {
          refetchData({
            pageNumber: currentPageNumber,
            onSuccess: () => {
              onObjectiveModificationSuccess({
                expandedRowIds,
                fetchObjectiveData,
                fetchGoalsData,
                rows,
                updateRowDataModifiedForArray,
              });
            },
          });
          dispatch(showSuccessSnackbar(`${objectivesName} deleted!`));
        },
        onLevelChange: () => {
          if (typeof functionRef.current === 'function') {
            functionRef.current();
          }
        },
        onReAlign: () => {
          refetchData({
            pageNumber: currentPageNumber,
            onSuccess: () => {
              onObjectiveModificationSuccess({
                expandedRowIds,
                fetchObjectiveData,
                fetchGoalsData,
                rows,
                updateRowDataModifiedForArray,
              });
            },
          });
          dispatch(showSuccessSnackbar(`${objectivesName} realigned!`));
        },
      })
    );

    sendOpenedObjectivePanelEvent({
      eventProperties: {
        source: 'Insights Page Overall',
      },
    });
  };

  useEffect(() => {
    if (isGoalPanelOfCreatedObjective && timeboundDetailsOfCreatedObjective) {
      const closeKRPanel = () => dispatch(closeCreateKRPanelAction());

      dispatch(
        openCreateKRPanelAction({
          onClose: closeKRPanel,
          onCancel: closeKRPanel,
          onSave: () => {
            refetchData({
              pageNumber: currentPageNumber,
              onSuccess: () => {
                onObjectiveModificationSuccess({
                  expandedRowIds,
                  fetchObjectiveData,
                  fetchGoalsData,
                  rows,
                  updateRowDataModifiedForArray,
                });
              },
            });
          },
          selectedTimeBoundPeriod: timeboundDetails,
          ownerEmployee: employee,
          subObjective: timeboundDetailsOfCreatedObjective,
          actionDispatchSource: isTeamHubView ? 'Team Page' : 'Insights',
          panelHeaderText: isTeamHubView ? 'Add Key Result' : 'Add goal',
        })
      );
    }
  }, [timeboundDetailsOfCreatedObjective, isGoalPanelOfCreatedObjective]);

  if (checkRabbitGoalsTasksFeatureFlagEnabled()) return <RabbitObjectiveDisplayComponent {...props} />;

  return (
    <ObjectiveNameGrid
      container
      alignItems="center"
      justify="initial"
      wrap="nowrap"
      style={{
        marginLeft: level ? `calc(${28 * level}px + ${2 * level * 3}rem)` : 0,
        alignItems: 'flex-start',
        padding: '1rem',
      }}
    >
      {isExpandedDerived && <DownLine level={level + 1} />}
      {
        // to draw vertical lines only for required levels
      }
      {levelsForWhichLinesToBeDrawn.map((_level, i) => (
        <Tline half={_level === level && isLastElement} noHorizontal={_level < level} key={_level} level={_level} />
      ))}
      {
        // the following line is for current level vertical line and horizontal line
      }
      {level > 0 && <Tline half={isLastElement} level={level} />}
      <StyledIconButton
        style={{ zIndex: '5' }}
        onClick={() => (isExpandedDerived ? collapseRow(uuid) : expandRow(uuid))}
        onMouseEnter={handleHoverMouseEnter}
        onMouseLeave={handleHoverMouseLeave}
      >
        <PlayIcon rotate={isExpandedDerived} />
      </StyledIconButton>
      {showRealignObjective && (
        <StyledRealignIconComp onClick={realignHandler} />
        // <div style={{ display: 'inline', position: 'absolute', left: '-0.75rem' }}>
        //   <StyledTooltip title="Realign">
        //     <ColoredIconButton
        //       onMouseEnter={handleHoverMouseEnter}
        //       onMouseLeave={handleHoverMouseLeave}
        //       onClick={realignHandler}
        //     >
        //       <RealignSvgIcon size="fixed12px" color="primary" />
        //     </ColoredIconButton>
        //   </StyledTooltip>
        // </div>
        // <RealignHoverContainer
        //   hoverComponent={
        //     <StyledTooltip title="Realign">
        //       <ColoredIconButton
        //         onMouseEnter={handleHoverMouseEnter}
        //         onMouseLeave={handleHoverMouseLeave}
        //         onClick={realignHandler}
        //       >
        //         <RealignSvgIcon size="fixed12px" color="primary" />
        //       </ColoredIconButton>
        //     </StyledTooltip>
        //   }
        //   pointerLocation={pointerLocation}
        //   handleMouseEnter={handleMouseEnter}
        //   handleMouseLeave={handleMouseLeave}
        //   handleHoverMouseEnter={handleHoverMouseEnter}
        //   handleHoverMouseLeave={handleHoverMouseLeave}
        // >
        //   <span></span>
        // </RealignHoverContainer>
      )}
      <>
        <ClickAwayListener onClickAway={() => setMenuIsOpen(false)}>
          <ColoredIconButton
            style={{ marginLeft: '1.25rem' }}
            ref={anchorRef}
            onClick={() => setMenuIsOpen((prev) => !prev)}
            onMouseEnter={handleHoverMouseEnter}
            onMouseLeave={handleHoverMouseLeave}
            id="nnnnnnnnn"
          >
            <PlusIcon3 size="fixed10px" color="blue" />
          </ColoredIconButton>
        </ClickAwayListener>
        <OKRMenuOptions
          addKR={() => {
            sendClickedOnCreateKRevent({
              eventProperties: {
                source: isTeamHubView ? 'Team Page' : 'Insights Page',
              },
            });

            const closeKRPanel = () => dispatch(closeCreateKRPanelAction());

            dispatch(
              openCreateKRPanelAction({
                onClose: closeKRPanel,
                onCancel: closeKRPanel,
                onSave: () => {
                  refetchData({
                    pageNumber: currentPageNumber,
                    onSuccess: () => {
                      onObjectiveModificationSuccess({
                        expandedRowIds,
                        fetchObjectiveData,
                        fetchGoalsData,
                        rows,
                        updateRowDataModifiedForArray,
                      });
                    },
                  });
                },
                selectedTimeBoundPeriod: timeboundDetails,
                ownerEmployee: employee,
                subObjective: timeboundDetails,
                actionDispatchSource: isTeamHubView ? 'Team Page' : 'Insights',
                panelHeaderText: isTeamHubView ? 'Add Key Result' : 'Add goal',
              })
            );
          }}
          addObjective={() => {
            expandRow(uuid);
            sendClickedOnCreateObjectiveEvent({
              eventProperties: {
                source: isTeamHubView ? 'Team Page' : 'Insights Page',
              },
            });
            setIsOpenCreateObjectivePanel(true);
          }}
          anchorEl={anchorRef}
          isOpen={menuIsOpen}
          setIsOpen={setMenuIsOpen}
          isSubObjectiveMenu
        />
      </>
      <StyledTooltip title={level > 0 ? 'Sub-objective' : 'Objective'}>
        <ObjectiveTwoToneSvgIcon
          style={{ marginLeft: '1.25rem', cursor: 'pointer' }}
          size="fixed24px"
          color={theme.palette.icons.purple}
        />
      </StyledTooltip>
      <FlexContainer
        style={{
          flexDirection: 'column',
          marginLeft: '1rem',
          flex: '1 1 auto',
        }}
      >
        <div>
          <StyledTooltip title={value?.length > 240 ? value : ''}>
            <FlexContainer style={{ alignItems: 'flex-start' }}>
              <FlexContainer style={{ flexDirection: 'column', flex: '1 1 auto' }}>
                <StyledTypography
                  variant="body2"
                  onClick={() => {
                    handleObjectiveNameClick(uuid);
                  }}
                  style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    webkitLineClamp: '2',
                    webkitBoxOrient: 'vertical',
                    zIndex: '5',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                  }}
                  id="goaaaaaal"
                  onMouseEnter={handleHoverMouseEnter}
                  onMouseLeave={handleHoverMouseLeave}
                >
                  {value}
                </StyledTypography>
                <SubTypeIcon />
              </FlexContainer>
              {isTeamHubView ? null : (
                <div style={{ flex: '0 0 auto' }}>
                  <ButtonOutlined
                    fontColor="primary"
                    borderColor="primary"
                    padding="6px"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      viewGoals({
                        uuid,
                        displayName: value,
                      });
                    }}
                  >
                    <DetailsSvgIcon size="fixed20px" />
                    <Typography12 leftGutter={1} weight={600}>
                      View Details
                    </Typography12>
                  </ButtonOutlined>
                </div>
              )}
            </FlexContainer>
          </StyledTooltip>
        </div>
      </FlexContainer>
      {isOpenCreateObjectivePanel && (
        <ObjectiveSidePanel
          open={isOpenCreateObjectivePanel}
          onCancel={() => {
            setTimeboundDetailsOfCreatedObjective(null);
            setIsGoalPanelOfCreatedObjective(false);
            setIsOpenCreateObjectivePanel(false);
          }}
          uuid={timeboundDetails.objectiveId}
          selectedTimeBoundPeriod={selectedTimeBoundPeriod}
          objectiveTimeBoundId={uuid}
          addNewObjective={addNewObjective}
          rows={rows}
          updateRowDataModifiedForArray={updateRowDataModifiedForArray}
          setIsOpenCreateGoalPanel={(value) => {
            setIsGoalPanelOfCreatedObjective(value);
          }}
          setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
          source={isTeamHubView ? 'Team Page' : 'Insights'}
        />
      )}
    </ObjectiveNameGrid>
  );
};

const AggregatedMetricsComponent = (props) => {
  const { value, summaryTitle, isEmployeeStateTable } = props;
  if (value === null)
    return (
      <StyledGrid container justify="center" alignItems="center">
        <EmptyBar />
      </StyledGrid>
    );

  const { COMPLETED_PERCENTAGE, PROGRESS_LEVEL } = value;

  const { barDistribution, hoverDetails, overall } = value;

  const hover = () => {
    if (hoverDetails) {
      const arrayOfCount = Object?.keys(hoverDetails)
        ?.sort((a, b) => GOAL_PROGRESS_OPTIONS_MAP[a]?.order - GOAL_PROGRESS_OPTIONS_MAP[b]?.order)
        ?.map((hoverDetail, index) => {
          if (hoverDetail && hoverDetails[hoverDetail]) {
            return `${GOAL_PROGRESS_OPTIONS_MAP[hoverDetail].label}: ${hoverDetails[hoverDetail]?.count}`;
          }
        })
        ?.filter((f) => f);

      return arrayOfCount?.reduce(
        (curr, prev, index) => curr + prev + (index === arrayOfCount?.length - 1 ? '' : ' • '),
        ''
      );
    }
  };

  if (isEmployeeStateTable) {
    return (
      <HoverAbleContainerComp
        displayInfoComp={<MetricInfoView objectiveName={summaryTitle} value={value} />}
        calibrateAble={false}
      >
        <StyledTypography
          variant="body2"
          weight={600}
          color={getColorFromKeyForAnalytics(GOAL_PROGRESS_OPTIONS_MAP[PROGRESS_LEVEL].color)}
        >
          {`${COMPLETED_PERCENTAGE}%`}
        </StyledTypography>
      </HoverAbleContainerComp>
    );
  }

  if (checkRabbitGoalsTasksFeatureFlagEnabled()) return <RabbitObjectiveTableAggregatedMetricsComponent {...props} />;

  return (
    <HoverAbleContainerComp
      displayInfoComp={
        <MetricInfoViewForGoalsRevamp objectiveName={summaryTitle} value={hoverDetails} overall={overall} />
      }
      calibrateAble={false}
    >
      <div style={{ width: '100%', height: '100%', padding: '1rem 2rem', paddingRight: '1.5rem' }}>
        <FlexContainer style={{ alignItems: 'flex-start' }}>
          <div style={{ width: '82%', height: '100%', marginRight: 'auto' }}>
            <StackedProgressBar data={barDistribution} />
          </div>
          <Typography13 customColor={theme.palette.text.heading}>
            {Math.round(parseFloat(overall?.avgCompletionPercentage))}%
          </Typography13>
        </FlexContainer>
        <div style={{ textAlign: 'left' }}>
          <Typography12 customColor={theme.palette.text.dark1}>{hover()}</Typography12>
        </div>
      </div>
    </HoverAbleContainerComp>
  );
};

const DISPLAY_COMPONENT_MAP = {
  COLOR_CODED_METRIC: ({ imageUrl, uuid, mangerUuid, value }) => {
    if (value === null)
      return (
        <StyledGrid container justify="center" alignItems="center">
          <EmptyBar />
        </StyledGrid>
      );

    const { COUNT, COLOR } = value;
    return (
      <StyledTypography variant="body2" color={getColorFromKeyForAnalytics(COLOR)}>
        {COUNT}
      </StyledTypography>
    );
  },
  EMPLOYEE_NAME: ({
    imageUrl,
    value,
    onDisplayNameClick = null,
    tooltipText = '',
    uuid,
    employee,
    actionButtonIcon,
    clickable = true,
  }) => {
    if (employee) {
      const { managerName, department, location, designation } = employee;
      return (
        <EmployeeHoverContainer
          hoverComponent={
            <EmployeeHover
              uuid={uuid}
              imageUrl={imageUrl}
              employeeName={value}
              managerName={managerName}
              designation={designation}
              department={department}
              location={location}
              actionButtonIcon={actionButtonIcon}
            />
          }
          clickable={Boolean(onDisplayNameClick) && clickable}
        >
          <NamedAvatar
            imageUrl={imageUrl}
            displayName={value}
            onDisplayNameClick={onDisplayNameClick}
            tooltipText={tooltipText}
            clickable={Boolean(onDisplayNameClick) && clickable}
            size="tiny"
            border={`1px solid ${theme.palette.borders.lighterGrey}`}
          />
        </EmployeeHoverContainer>
      );
    }

    return (
      <NamedAvatar
        imageUrl={imageUrl}
        displayName={value}
        onDisplayNameClick={onDisplayNameClick}
        tooltipText={tooltipText}
        clickable={Boolean(onDisplayNameClick)}
      />
    );
  },
  EMPLOYEE_DEPARTMENT: ({ value }) => <InfoText text={value} />,
  AGGREGATED_METRICS: AggregatedMetricsComponent,
  AGGREGATED_METRICS_BAR: ({ value, summaryTitle }) => {
    if (value === null)
      return (
        <StyledGrid container justify="center" alignItems="center">
          <EmptyBar />
        </StyledGrid>
      );

    const { COMPLETED_PERCENTAGE, PROGRESS_LEVEL } = value;

    return (
      <HoverAbleContainerComp displayInfoComp={<MetricInfoView objectiveName={summaryTitle} value={value} />}>
        <StyledBox width="75%" display="flex">
          <ProgressBarWithPercentage
            color={GOAL_PROGRESS_OPTIONS_MAP[PROGRESS_LEVEL].color}
            completionPercentage={COMPLETED_PERCENTAGE}
          />
        </StyledBox>
      </HoverAbleContainerComp>
    );
  },
  DONUT_GRAPH: ({ value, summaryTitle }) => {
    if (value === null)
      return (
        <StyledGrid container justify="center" alignItems="center">
          <EmptyBar />
        </StyledGrid>
      );

    const { AT_RISK: atRisKObj, CLOSED: closedObj, OFF_TRACK: offTrackObj, ON_TRACK: onTrackObj } = value;

    const chartData = [
      {
        count: onTrackObj.COUNT,
        color: getColorFromKeyForAnalytics(GOAL_PROGRESS_OPTIONS_MAP.ON_TRACK.color),
      },
      {
        count: offTrackObj.COUNT,
        color: getColorFromKeyForAnalytics(GOAL_PROGRESS_OPTIONS_MAP.OFF_TRACK.color),
      },
      {
        count: atRisKObj.COUNT,
        color: getColorFromKeyForAnalytics(GOAL_PROGRESS_OPTIONS_MAP.AT_RISK.color),
      },
      {
        count: closedObj.COUNT,
        color: getColorFromKeyForAnalytics('primary'),
      },
    ];

    return (
      <HoverAbleContainerComp
        displayInfoComp={<DonutChartInfoView objectiveName={summaryTitle} value={value} />}
        calibrateAble={false}
      >
        <StyledBox width="72px" display="flex" justifyContent="center">
          <MeshDonutChart data={chartData} />
        </StyledBox>
      </HoverAbleContainerComp>
    );
  },
  COLOR_CODED_DATE: ({ value }) => {
    if (value === null)
      return (
        <StyledGrid container justify="center" alignItems="center">
          <StyledBox padding="1rem 2rem">
            <EmptyBar />
          </StyledBox>
        </StyledGrid>
      );

    const { COLOR, DATE } = value;
    return <ColoredDate bgColor={`${getColorFromKeyForAnalytics(COLOR)}40`} textColor={COLOR} date={new Date(DATE)} />;
  },
  COLORED_PROGRESS_PERCENTAGE: ({ value }) => {
    if (value === null) {
      return (
        <StyledGrid container justify="center" alignItems="center">
          <EmptyBar />
        </StyledGrid>
      );
    }

    const { COMPLETED_PERCENTAGE, COLOR } = value;
    return (
      <ProgressBarWithPercentage
        completionPercentage={COMPLETED_PERCENTAGE}
        color={getColorFromKeyForAnalytics(COLOR)}
      />
    );
  },
  OBJECTIVE: ObjectiveDisplayComponent,
  DEFAULT: ({ value }) => <InfoText text={value} />,
  COMPETENCY: ({ value, onDisplayNameClick = null }) => (
    <StyledTypography
      variant="body2"
      color="textPrimary"
      texttransform="capitalize"
      weight="bold"
      onClick={onDisplayNameClick}
    >
      {value}
    </StyledTypography>
  ),
  COMPETENCY_TAG: () => null,
  COMPETENCY_SCORE: ({ score, color }) => {
    if (!score || !color) {
      return (
        <StyledGrid container justify="center" alignItems="center">
          <EmptyBar />
        </StyledGrid>
      );
    }

    return <ProgressBarWithPercentage completionPercentage={score} color={getColorFromKeyForAnalytics(color)} />;
  },
  COMPETENCY_COUNT: ({ count, color, width = '100%', tooltipText }) => {
    if (count === null || count === undefined) {
      return (
        <StyledGrid container justify="center" alignItems="center">
          <EmptyBar />
        </StyledGrid>
      );
    }

    return (
      <StyledTooltip title={tooltipText}>
        <div>
          <ColoredBgText
            textColor={getColorFromKeyForAnalytics(color)}
            bgColor={`${getColorFromKeyForAnalytics(color)}40`}
            text={count}
            width={width}
          />
        </div>
      </StyledTooltip>
    );
  },
  ENGAGEMENT_DRIVER: ({ value, onDisplayNameClick = null }) => (
    <StyledTypography
      variant="body2"
      color={theme.palette.text.heading}
      texttransform="capitalize"
      onClick={onDisplayNameClick}
    >
      {value}
    </StyledTypography>
  ),
  ENGAGEMENT_DONUT_GRAPH: ({ value, summaryTitle }) => {
    if (value === null)
      return (
        <StyledGrid container justify="center" alignItems="center">
          <StyledTooltip title="No data">
            <EmptyBar />
          </StyledTooltip>
        </StyledGrid>
      );

    const chartData = Object.values(value).map(({ COLOR, COUNT }) => ({
      count: COUNT,
      color: getColorFromKeyForAnalytics(COLOR),
    }));

    const hoverComponentData = Object.entries(value).map(([key, valueObj], index) => ({
      key,
      ...valueObj,
    }));

    return (
      <HoverAbleContainerComp
        displayInfoComp={<EngagementDonutChartHover data={hoverComponentData} />}
        calibrateAble={false}
      >
        <StyledBox width="72px" display="flex" justifyContent="center">
          <MeshDonutChart data={chartData} />
        </StyledBox>
      </HoverAbleContainerComp>
    );
  },
  ENGAGEMENT_RATING_HOVER: ({ value, summaryTitle }) => {
    const NoDataComp = () => (
      <StyledGrid container justify="center" alignItems="center">
        <StyledTooltip title="No data">
          <EmptyBar />
        </StyledTooltip>
      </StyledGrid>
    );

    if (value === null) return <NoDataComp />;

    const { RATING_DISTRIBUTION, RATING } = value;
    if (RATING_DISTRIBUTION === null || RATING === null) {
      return <NoDataComp />;
    }

    const hoverComponentData = Object.entries(RATING_DISTRIBUTION).map(([key, valueObj], index) => ({
      key,
      ...valueObj,
    }));

    const { COUNT, COLOR } = RATING;

    return (
      <HoverAbleContainerComp
        displayInfoComp={<EngagementDonutChartHover data={hoverComponentData} />}
        calibrateAble={false}
      >
        <StyledTypography variant="body2" color={getColorFromKeyForAnalytics(COLOR)}>
          {COUNT}
        </StyledTypography>
      </HoverAbleContainerComp>
    );
  },
  ENGAGEMENT_QUESTION: ({ value }) => (
    <AbsoluteWidthTextContainer>
      <StyledTypography variant="body2" color={theme.palette.text.heading} truncate>
        {value}
      </StyledTypography>
    </AbsoluteWidthTextContainer>
  ),
  ONE_ON_ONE_RATING: ({ value, managerName = 'Manager' }) => {
    const { answerType, overallRating, totalCount, hoverDetails } = value;

    const RatingComp = (
      <HoverAbleContainerComp
        displayInfoComp={
          <RatingHover managerName={managerName} count={totalCount} ratingDistributionData={hoverDetails} />
        }
        calibrateAble={false}
      >
        <StyledBox display="flex" justifyContent="center" alignItems="center">
          <StarIcon size="headerSmall" color="mustard" />
          <StyledTypography variant="body2" color="textSecondary" leftGutter={1}>
            {overallRating}
          </StyledTypography>
        </StyledBox>
      </HoverAbleContainerComp>
    );

    const SmileyComp = (
      <HoverAbleContainerComp
        displayInfoComp={
          <SmileyHover managerName={managerName} count={totalCount} smileyDistributionData={hoverDetails} />
        }
        calibrateAble={false}
      >
        <StyledBox display="flex" justifyContent="center" alignItems="center">
          {getSmileyComponent(Math.round(overallRating))}
        </StyledBox>
      </HoverAbleContainerComp>
    );

    return (
      <>
        {value?.overallRating ? (
          answerType === 'EMOJI' ? (
            SmileyComp
          ) : (
            RatingComp
          )
        ) : (
          <StyledBox display="flex" justifyContent="center" alignItems="center">
            <EmptyBar />
          </StyledBox>
        )}
      </>
    );
  },
  OWNER_AVATAR_GROUP: () => <div />,
  // Custom column, Objective does not have weightage, that's why returning null.
  // This is required because of the table headers format.
  // Actual weightage comes in the KR row
  KR_WEIGHTAGE: () => null,
};

const RealignIconButton = styled(ColoredIconButton)`
  visibility: hidden;
`;

const ObjectiveNameGrid = styled(StyledGrid)`
  width: auto;
`;

const DISPLAY_COMPONENT_META = {
  COLOR_CODED_METRIC: {
    id: 'COLOR_CODED_METRIC',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  EMPLOYEE_NAME: {
    id: 'EMPLOYEE_NAME',
    isSortable: true,
    align: 'left',
    width: '40%',
  },
  EMPLOYEE_DEPARTMENT: {
    id: 'EMPLOYEE_DEPARTMENT',
    isSortable: false,
    align: 'left',
    width: '30%',
  },
  AGGREGATED_METRICS: {
    id: 'AGGREGATED_METRICS',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  AGGREGATED_METRICS_BAR: {
    id: 'AGGREGATED_METRICS_BAR',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  DONUT_GRAPH: {
    id: 'DONUT_GRAPH',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  COLOR_CODED_DATE: {
    id: 'COLOR_CODED_DATE',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  COLORED_PROGRESS_PERCENTAGE: {
    id: 'COLORED_PROGRESS_PERCENTAGE',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  OBJECTIVE: {
    id: 'OBJECTIVE',
    isSortable: true,
    align: 'left',
    width: '50%',
  },
  DEFAULT: {
    id: 'DEFAULT',
    isSortable: false,
    align: 'left',
    width: 'auto',
  },
  COMPETENCY: {
    id: 'COMPETENCY',
    isSortable: true,
    align: 'left',
    width: '50%',
  },
  COMPETENCY_TAG: {
    id: 'COMPETENCY_TAG',
    isSortable: false,
    align: 'left',
    width: 'auto',
  },
  COMPETENCY_SCORE: {
    id: 'COMPETENCY_SCORE',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  COMPETENCY_COUNT: {
    id: 'COMPETENCY_COUNT',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  ENGAGEMENT_DRIVER: {
    id: 'ENGAGEMENT_DRIVER',
    isSortable: true,
    align: 'left',
    width: '50%',
  },
  ENGAGEMENT_DONUT_GRAPH: {
    id: 'ENGAGEMENT_DONUT_GRAPH',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  ENGAGEMENT_QUESTION: {
    id: 'ENGAGEMENT_QUESTION',
    isSortable: true,
    align: 'left',
    width: '50%',
  },
  ONE_ON_ONE_RATING: {
    id: 'ONE_ON_ONE_RATING',
    isSortable: true,
    align: 'center',
    width: 'auto',
  },
  OWNER_AVATAR_GROUP: {
    id: 'OWNER_AVATAR_GROUP',
    isSortable: false,
    align: 'center',
    width: 'auto',
  },
};

export default DISPLAY_COMPONENT_MAP;
export { DISPLAY_COMPONENT_META };
